import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Building2,
  Bell,
  Scan,
  BarChart3,
  GitPullRequest,
  ShieldOff,
  Users,
  Database,
  Palette,
  Save,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Globe,
  Clock,
  GitBranch,
  Mail,
  MessageSquare,
  Zap,
  Calendar,
  AlertTriangle,
  Code2,
  Package,
  Key,
  FileCode,
  ScrollText,
  Container,
  Sliders,
  Shield,
  FileX,
  Timer,
  Settings2,
  Scale,
  FileWarning,
  Trash2,
  RefreshCw,
  HelpCircle,
  ExternalLink,
  Info,
  X,
} from "lucide-react";
import Layout, { PageHeader, PageContainer } from "../components/ui/Layout";
import { apiFetch } from "../api/client";
import { cn } from "../lib/utils";

// shadcn/ui components
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/shadcn/card";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../components/shadcn/tabs";
import { Input } from "../components/shadcn/input";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "../components/shadcn/select";
import { Switch } from "../components/shadcn/switch";
import { Separator } from "../components/shadcn/separator";
import { Skeleton } from "../components/shadcn/skeleton";
import { Button } from "../components/shadcn/button";
import { Badge } from "../components/shadcn/badge";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface OrgSettings {
  // Organization
  orgName: string;
  orgSlug: string;
  defaultTimezone: string;
  defaultBaseBranch: string;
  perRepoOverrides: boolean;
  featurePreviews: string[];

  // Notifications
  emailNotifications: boolean;
  notificationRecipients: string[];
  immediateAlertThreshold: string;
  digestFrequency: string;
  scanFailureAlerts: boolean;

  // Scanning
  enabledScanTypes: string[];
  prScanTriggers: string[];
  baselineScanSchedule: string;
  firstBaselineBehavior: string;
  diffAwareMode: string;
  smartFingerprintMode: string;
  excludedPaths: string[];
  maxFileSize: number;
  skipBinaries: boolean;
  scanTimeout: number;
  maxFindings: number;
  configAsCode: boolean;
  configPath: string;
  sastRuleset: string;
  scaDependencyDiscovery: string;
  secretDetectionSensitivity: string;
  iacCoverage: string[];
  containerScanMode: string;

  // Priority Scoring
  priorityScoringProfile: string;
  cvssWeight: number;
  epssWeight: number;
  kevWeight: number;
  reachabilityWeight: number;
  epssElevationThreshold: number;
  kevOverride: boolean;
  reachabilityMode: string;
  testPenalty: number;
  generatedCodePenalty: number;
  exampleCodePenalty: number;
  prVisibilityThreshold: number;
  mergeBlockThreshold: number;

  // PR/CI
  prCommentStyle: string;
  maxInlineComments: number;
  updateExistingComment: boolean;
  githubCheckRun: boolean;
  mergeGatingMode: string;
  autoCreateTickets: boolean;

  // Suppressions
  allowSuppressions: boolean;
  suppressionRequiresReason: boolean;
  suppressionRequiresExpiration: boolean;
  maxSuppressionDuration: number;
  suppressionApprovalRules: string;

  // Policy
  policyMode: string;
  requiredScanCoverage: string[];
  licensePolicyAction: string;
  forbiddenLicenses: string[];
  unknownLicenseHandling: string;

  // Data Retention
  findingsRetentionDays: number;
  scanArtifactRetentionDays: number;
  operationalLogRetentionDays: number;
  secretDataHandling: string;

  // UI
  theme: string;
  defaultLandingPage: string;
}

const defaultSettings: OrgSettings = {
  orgName: "My Organization",
  orgSlug: "my-org",
  defaultTimezone: "UTC",
  defaultBaseBranch: "main",
  perRepoOverrides: true,
  featurePreviews: [],

  emailNotifications: true,
  notificationRecipients: ["pr_author", "security_team"],
  immediateAlertThreshold: "critical_only",
  digestFrequency: "weekly",
  scanFailureAlerts: true,

  enabledScanTypes: ["sast", "sca", "secrets", "iac", "license"],
  prScanTriggers: ["opened", "synchronize", "reopened"],
  baselineScanSchedule: "nightly",
  firstBaselineBehavior: "auto",
  diffAwareMode: "new_only",
  smartFingerprintMode: "balanced",
  excludedPaths: ["node_modules/", "vendor/", "dist/", "build/", "coverage/"],
  maxFileSize: 2,
  skipBinaries: true,
  scanTimeout: 300,
  maxFindings: 500,
  configAsCode: true,
  configPath: ".paxley.yml",
  sastRuleset: "recommended",
  scaDependencyDiscovery: "auto",
  secretDetectionSensitivity: "balanced",
  iacCoverage: ["terraform", "kubernetes", "cloudformation"],
  containerScanMode: "both",

  priorityScoringProfile: "balanced",
  cvssWeight: 45,
  epssWeight: 25,
  kevWeight: 20,
  reachabilityWeight: 10,
  epssElevationThreshold: 0.1,
  kevOverride: true,
  reachabilityMode: "conservative",
  testPenalty: -10,
  generatedCodePenalty: -20,
  exampleCodePenalty: -10,
  prVisibilityThreshold: 70,
  mergeBlockThreshold: 85,

  prCommentStyle: "summary_inline",
  maxInlineComments: 10,
  updateExistingComment: true,
  githubCheckRun: true,
  mergeGatingMode: "warn_only",
  autoCreateTickets: false,

  allowSuppressions: true,
  suppressionRequiresReason: true,
  suppressionRequiresExpiration: true,
  maxSuppressionDuration: 90,
  suppressionApprovalRules: "high_critical_require_approval",

  policyMode: "advisory",
  requiredScanCoverage: ["sast", "sca", "secrets", "iac"],
  licensePolicyAction: "warn",
  forbiddenLicenses: ["AGPL-3.0", "GPL-3.0", "SSPL-1.0"],
  unknownLicenseHandling: "warn",

  findingsRetentionDays: 365,
  scanArtifactRetentionDays: 30,
  operationalLogRetentionDays: 30,
  secretDataHandling: "masked_only",

  theme: "system",
  defaultLandingPage: "priority_queue",
};

// ─────────────────────────────────────────────────────────────────────────────
// Settings tabs configuration
// ─────────────────────────────────────────────────────────────────────────────

const tabsConfig = [
  { id: "organization", label: "Organization", icon: Building2 },
  { id: "notifications", label: "Notifications", icon: Bell },
  { id: "scanning", label: "Scanning", icon: Scan },
  { id: "priority", label: "Priority Scoring", icon: BarChart3 },
  { id: "pr-ci", label: "PR & CI", icon: GitPullRequest },
  { id: "suppressions", label: "Suppressions", icon: ShieldOff },
  { id: "policy", label: "Policy", icon: Scale },
  { id: "data", label: "Data & Privacy", icon: Database },
  { id: "display", label: "Display", icon: Palette },
] as const;

type TabId = (typeof tabsConfig)[number]["id"];

// ─────────────────────────────────────────────────────────────────────────────
// Reusable Components
// ─────────────────────────────────────────────────────────────────────────────

function SettingRow({
  label,
  description,
  children,
  hint,
}: {
  label: string;
  description?: string;
  children: React.ReactNode;
  hint?: string;
}) {
  return (
    <div className="flex flex-col md:flex-row md:items-start gap-3 md:gap-6">
      <div className="md:w-1/3 flex-shrink-0">
        <label className="block text-sm font-medium text-foreground">
          {label}
        </label>
        {description && (
          <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
        )}
      </div>
      <div className="md:flex-1">
        {children}
        {hint && (
          <p className="text-xs text-muted-foreground mt-1.5 flex items-start gap-1">
            <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
            {hint}
          </p>
        )}
      </div>
    </div>
  );
}

function ShadcnSelect({
  value,
  onChange,
  options,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  className?: string;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={cn("w-full max-w-md", className)}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function MultiSelect({
  values,
  onChange,
  options,
}: {
  values: string[];
  onChange: (values: string[]) => void;
  options: { value: string; label: string; icon?: React.ReactNode }[];
}) {
  const toggleOption = (value: string) => {
    if (values.includes(value)) {
      onChange(values.filter((v) => v !== value));
    } else {
      onChange([...values, value]);
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const isSelected = values.includes(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => toggleOption(opt.value)}
            className={cn(
              "px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors border",
              isSelected
                ? "bg-primary/10 text-primary border-primary/30"
                : "bg-secondary text-muted-foreground hover:bg-secondary/80 border-transparent"
            )}
          >
            {opt.icon}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function Slider({
  value,
  onChange,
  min,
  max,
  step = 1,
  suffix = "",
}: {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step?: number;
  suffix?: string;
}) {
  return (
    <div className="flex items-center gap-4">
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="flex-1 h-2 bg-secondary rounded-lg appearance-none cursor-pointer accent-primary"
      />
      <span className="text-sm font-mono text-foreground w-16 text-right">
        {value}
        {suffix}
      </span>
    </div>
  );
}

function WeightSliders({
  weights,
  onChange,
}: {
  weights: { cvss: number; epss: number; kev: number; reachability: number };
  onChange: (weights: {
    cvss: number;
    epss: number;
    kev: number;
    reachability: number;
  }) => void;
}) {
  const total =
    weights.cvss + weights.epss + weights.kev + weights.reachability;

  const updateWeight = (
    key: "cvss" | "epss" | "kev" | "reachability",
    newValue: number
  ) => {
    // Clamp to prevent negative values in others
    const clampedValue = Math.min(newValue, 100 - 5 * 3);
    onChange({ ...weights, [key]: clampedValue });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between text-sm">
        <span className="text-muted-foreground">Weight Distribution</span>
        <span
          className={cn(
            "font-mono",
            total === 100 ? "text-green-500" : "text-yellow-500"
          )}
        >
          Total: {total}%
        </span>
      </div>

      <div className="space-y-3">
        {[
          {
            key: "cvss" as const,
            label: "CVSS Score",
            color: "text-red-500",
          },
          {
            key: "epss" as const,
            label: "EPSS (Exploit Probability)",
            color: "text-yellow-500",
          },
          {
            key: "kev" as const,
            label: "KEV (Known Exploited)",
            color: "text-orange-500",
          },
          {
            key: "reachability" as const,
            label: "Reachability",
            color: "text-primary",
          },
        ].map(({ key, label, color }) => (
          <div key={key} className="space-y-1">
            <div className="flex justify-between text-sm">
              <span className={color}>{label}</span>
              <span className="font-mono text-foreground">{weights[key]}%</span>
            </div>
            <input
              type="range"
              min={5}
              max={70}
              value={weights[key]}
              onChange={(e) => updateWeight(key, Number(e.target.value))}
              className={cn(
                "w-full h-2 bg-secondary rounded-lg appearance-none cursor-pointer accent-current",
                color
              )}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function TagInput({
  values,
  onChange,
  placeholder = "Add item...",
}: {
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
}) {
  const [input, setInput] = useState("");

  const addTag = () => {
    const trimmed = input.trim();
    if (trimmed && !values.includes(trimmed)) {
      onChange([...values, trimmed]);
      setInput("");
    }
  };

  const removeTag = (tag: string) => {
    onChange(values.filter((v) => v !== tag));
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addTag();
            }
          }}
          placeholder={placeholder}
          className="flex-1"
        />
        <Button
          type="button"
          variant="secondary"
          onClick={addTag}
          disabled={!input.trim()}
        >
          Add
        </Button>
      </div>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {values.map((tag) => (
            <Badge
              key={tag}
              variant="secondary"
              className="gap-1 pl-2.5 pr-1.5 py-1"
            >
              {tag}
              <button
                type="button"
                onClick={() => removeTag(tag)}
                className="text-muted-foreground hover:text-destructive ml-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Loading Skeleton
// ─────────────────────────────────────────────────────────────────────────────

function SettingsLoadingSkeleton() {
  return (
    <div className="space-y-6">
      {Array.from({ length: 3 }).map((_, i) => (
        <Card key={i}>
          <CardHeader>
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-4 w-64" />
          </CardHeader>
          <CardContent className="space-y-6">
            {Array.from({ length: 3 }).map((_, j) => (
              <div key={j} className="flex flex-col md:flex-row gap-4">
                <div className="md:w-1/3 space-y-1">
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-3 w-40" />
                </div>
                <div className="md:flex-1">
                  <Skeleton className="h-10 w-full max-w-md" />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab Content Components
// ─────────────────────────────────────────────────────────────────────────────

function OrganizationTab({
  settings,
  updateSetting,
}: {
  settings: OrgSettings;
  updateSetting: <K extends keyof OrgSettings>(
    key: K,
    value: OrgSettings[K]
  ) => void;
}) {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Organization Details</CardTitle>
          <CardDescription>
            Basic information about your organization
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <SettingRow
            label="Organization Name"
            description="Display name used across dashboard and reports"
          >
            <Input
              value={settings.orgName}
              onChange={(e) => updateSetting("orgName", e.target.value)}
              className="max-w-md"
              placeholder="My Organization"
            />
          </SettingRow>

          <Separator />

          <SettingRow
            label="Organization Slug"
            description="URL-safe identifier for API and exports"
          >
            <div className="flex items-center gap-2">
              <Input
                value={settings.orgSlug}
                readOnly
                className="max-w-md bg-muted cursor-not-allowed"
              />
              <Button variant="secondary" size="sm">
                Change
              </Button>
            </div>
          </SettingRow>

          <Separator />

          <SettingRow
            label="Default Time Zone"
            description="Controls timestamps in findings, charts, and digests"
          >
            <ShadcnSelect
              value={settings.defaultTimezone}
              onChange={(v) => updateSetting("defaultTimezone", v)}
              options={[
                { value: "UTC", label: "UTC" },
                { value: "America/New_York", label: "Eastern Time (US)" },
                { value: "America/Chicago", label: "Central Time (US)" },
                { value: "America/Denver", label: "Mountain Time (US)" },
                {
                  value: "America/Los_Angeles",
                  label: "Pacific Time (US)",
                },
                { value: "Europe/London", label: "London (UK)" },
                { value: "Europe/Paris", label: "Central European Time" },
                { value: "Asia/Tokyo", label: "Japan Standard Time" },
                { value: "Asia/Singapore", label: "Singapore Time" },
                {
                  value: "Australia/Sydney",
                  label: "Australian Eastern Time",
                },
              ]}
            />
          </SettingRow>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Default Behavior</CardTitle>
          <CardDescription>
            Organization-wide defaults for scanning
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <SettingRow
            label="Default Baseline Branch"
            description="Branch used for baseline scans and PR comparisons"
          >
            <Input
              value={settings.defaultBaseBranch}
              onChange={(e) =>
                updateSetting("defaultBaseBranch", e.target.value)
              }
              className="max-w-md"
              placeholder="main"
            />
          </SettingRow>

          <Separator />

          <SettingRow
            label="Per-Repository Overrides"
            description="Allow repos to override org-level settings"
          >
            <Switch
              checked={settings.perRepoOverrides}
              onCheckedChange={(v) => updateSetting("perRepoOverrides", v)}
            />
          </SettingRow>

          <Separator />

          <SettingRow
            label="Feature Previews"
            description="Beta features available for testing"
          >
            <MultiSelect
              values={settings.featurePreviews}
              onChange={(v) => updateSetting("featurePreviews", v)}
              options={[
                { value: "gitlab_beta", label: "GitLab Beta" },
                { value: "bitbucket_beta", label: "Bitbucket Beta" },
                {
                  value: "advanced_reachability",
                  label: "Advanced Reachability",
                },
                { value: "autofix_prs", label: "Auto-fix PRs" },
              ]}
            />
          </SettingRow>
        </CardContent>
      </Card>
    </div>
  );
}

function NotificationsTab({
  settings,
  updateSetting,
}: {
  settings: OrgSettings;
  updateSetting: <K extends keyof OrgSettings>(
    key: K,
    value: OrgSettings[K]
  ) => void;
}) {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Email Notifications</CardTitle>
          <CardDescription>
            Configure email alerts and digests
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <SettingRow
            label="Email Notifications"
            description="Enable email alerts for the organization"
          >
            <Switch
              checked={settings.emailNotifications}
              onCheckedChange={(v) => updateSetting("emailNotifications", v)}
            />
          </SettingRow>

          <Separator />

          <SettingRow
            label="Notification Recipients"
            description="Who receives alerts for new findings"
          >
            <MultiSelect
              values={settings.notificationRecipients}
              onChange={(v) => updateSetting("notificationRecipients", v)}
              options={[
                { value: "pr_author", label: "PR Author" },
                { value: "commit_author", label: "Commit Author" },
                { value: "pr_reviewers", label: "PR Reviewers" },
                { value: "codeowners", label: "CODEOWNERS" },
                { value: "security_team", label: "Security Team" },
              ]}
            />
          </SettingRow>

          <Separator />

          <SettingRow
            label="Immediate Alert Threshold"
            description="When to send interrupt notifications"
          >
            <ShadcnSelect
              value={settings.immediateAlertThreshold}
              onChange={(v) => updateSetting("immediateAlertThreshold", v)}
              options={[
                { value: "p0_85", label: "P0 Score >= 85" },
                { value: "critical_only", label: "Critical Only" },
                { value: "critical_high", label: "Critical + High" },
                { value: "all", label: "All Severities" },
                { value: "none", label: "Never (Digest Only)" },
              ]}
            />
          </SettingRow>

          <Separator />

          <SettingRow
            label="Digest Frequency"
            description="How often to send summary emails"
          >
            <ShadcnSelect
              value={settings.digestFrequency}
              onChange={(v) => updateSetting("digestFrequency", v)}
              options={[
                { value: "daily", label: "Daily" },
                { value: "weekly", label: "Weekly" },
                { value: "monthly", label: "Monthly" },
                { value: "off", label: "Off" },
              ]}
            />
          </SettingRow>

          <Separator />

          <SettingRow
            label="Scan Failure Alerts"
            description="Notify when scans fail or time out"
          >
            <Switch
              checked={settings.scanFailureAlerts}
              onCheckedChange={(v) => updateSetting("scanFailureAlerts", v)}
            />
          </SettingRow>
        </CardContent>
      </Card>
    </div>
  );
}

function ScanningTab({
  settings,
  updateSetting,
}: {
  settings: OrgSettings;
  updateSetting: <K extends keyof OrgSettings>(
    key: K,
    value: OrgSettings[K]
  ) => void;
}) {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Enabled Scanners</CardTitle>
          <CardDescription>
            Select which security scanners to run
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <SettingRow
            label="Scan Types"
            description="Enable or disable specific scanners"
          >
            <MultiSelect
              values={settings.enabledScanTypes}
              onChange={(v) => updateSetting("enabledScanTypes", v)}
              options={[
                {
                  value: "sast",
                  label: "SAST",
                  icon: <Code2 className="h-4 w-4" />,
                },
                {
                  value: "sca",
                  label: "Dependencies",
                  icon: <Package className="h-4 w-4" />,
                },
                {
                  value: "secrets",
                  label: "Secrets",
                  icon: <Key className="h-4 w-4" />,
                },
                {
                  value: "iac",
                  label: "IaC",
                  icon: <FileCode className="h-4 w-4" />,
                },
                {
                  value: "license",
                  label: "License",
                  icon: <ScrollText className="h-4 w-4" />,
                },
                {
                  value: "container",
                  label: "Container",
                  icon: <Container className="h-4 w-4" />,
                },
              ]}
            />
          </SettingRow>

          <Separator />

          <SettingRow
            label="SAST Ruleset"
            description="Which rules to use for static analysis"
          >
            <ShadcnSelect
              value={settings.sastRuleset}
              onChange={(v) => updateSetting("sastRuleset", v)}
              options={[
                {
                  value: "recommended",
                  label: "Paxley Recommended (Low Noise)",
                },
                { value: "strict", label: "Strict (More Coverage)" },
                { value: "compliance", label: "Compliance-Focused" },
                { value: "custom", label: "Custom Rules Repo" },
              ]}
            />
          </SettingRow>

          <Separator />

          <SettingRow
            label="Secret Detection Sensitivity"
            description="Tune false positive vs coverage tradeoff"
          >
            <ShadcnSelect
              value={settings.secretDetectionSensitivity}
              onChange={(v) =>
                updateSetting("secretDetectionSensitivity", v)
              }
              options={[
                {
                  value: "conservative",
                  label: "Conservative (Fewer False Positives)",
                },
                { value: "balanced", label: "Balanced" },
                {
                  value: "aggressive",
                  label: "Aggressive (More Coverage)",
                },
              ]}
            />
          </SettingRow>

          <Separator />

          <SettingRow
            label="IaC Coverage"
            description="Infrastructure-as-Code formats to scan"
          >
            <MultiSelect
              values={settings.iacCoverage}
              onChange={(v) => updateSetting("iacCoverage", v)}
              options={[
                { value: "terraform", label: "Terraform" },
                { value: "cloudformation", label: "CloudFormation" },
                { value: "kubernetes", label: "Kubernetes/Helm" },
                { value: "ansible", label: "Ansible" },
                { value: "dockerfile", label: "Dockerfile" },
              ]}
            />
          </SettingRow>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Scan Triggers</CardTitle>
          <CardDescription>
            When to run scans automatically
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <SettingRow
            label="PR Scan Triggers"
            description="GitHub events that trigger PR scans"
          >
            <MultiSelect
              values={settings.prScanTriggers}
              onChange={(v) => updateSetting("prScanTriggers", v)}
              options={[
                { value: "opened", label: "Opened" },
                { value: "synchronize", label: "New Commits" },
                { value: "reopened", label: "Reopened" },
                { value: "ready_for_review", label: "Ready for Review" },
                { value: "labeled", label: "Labeled" },
              ]}
            />
          </SettingRow>

          <Separator />

          <SettingRow
            label="Baseline Scan Schedule"
            description="How often to run full repository scans"
          >
            <ShadcnSelect
              value={settings.baselineScanSchedule}
              onChange={(v) => updateSetting("baselineScanSchedule", v)}
              options={[
                { value: "off", label: "Off (Manual Only)" },
                { value: "nightly", label: "Nightly" },
                { value: "weekly", label: "Weekly" },
                { value: "custom", label: "Custom Cron" },
              ]}
            />
          </SettingRow>

          <Separator />

          <SettingRow
            label="Diff-Aware Reporting"
            description="What to show in PR feedback"
          >
            <ShadcnSelect
              value={settings.diffAwareMode}
              onChange={(v) => updateSetting("diffAwareMode", v)}
              options={[
                {
                  value: "new_only",
                  label: "New Issues Only (Recommended)",
                },
                { value: "new_fixed", label: "New + Fixed Summary" },
                { value: "all", label: "Show All (Noisy)" },
              ]}
            />
          </SettingRow>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Scan Scope</CardTitle>
          <CardDescription>Control what gets scanned</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <SettingRow
            label="Excluded Paths"
            description="Directories and patterns to skip"
          >
            <TagInput
              values={settings.excludedPaths}
              onChange={(v) => updateSetting("excludedPaths", v)}
              placeholder="Add path (e.g., vendor/)"
            />
          </SettingRow>

          <Separator />

          <SettingRow
            label="Max File Size"
            description="Skip files larger than this"
          >
            <Slider
              value={settings.maxFileSize}
              onChange={(v) => updateSetting("maxFileSize", v)}
              min={1}
              max={10}
              suffix=" MB"
            />
          </SettingRow>

          <Separator />

          <SettingRow
            label="Skip Binaries"
            description="Ignore binary and archive files"
          >
            <Switch
              checked={settings.skipBinaries}
              onCheckedChange={(v) => updateSetting("skipBinaries", v)}
            />
          </SettingRow>

          <Separator />

          <SettingRow
            label="Scan Timeout"
            description="Maximum time per scanner"
          >
            <Slider
              value={settings.scanTimeout}
              onChange={(v) => updateSetting("scanTimeout", v)}
              min={60}
              max={600}
              step={30}
              suffix="s"
            />
          </SettingRow>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Configuration as Code</CardTitle>
          <CardDescription>
            Allow repository-level configuration files
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <SettingRow
            label="Enable Config Files"
            description="Let repos define .paxley.yml overrides"
          >
            <Switch
              checked={settings.configAsCode}
              onCheckedChange={(v) => updateSetting("configAsCode", v)}
            />
          </SettingRow>

          <Separator />

          <SettingRow
            label="Config File Path"
            description="Path to look for config"
          >
            <Input
              value={settings.configPath}
              onChange={(e) => updateSetting("configPath", e.target.value)}
              className="max-w-md font-mono text-sm"
              placeholder=".paxley.yml"
            />
          </SettingRow>
        </CardContent>
      </Card>
    </div>
  );
}

function PriorityScoringTab({
  settings,
  updateSetting,
}: {
  settings: OrgSettings;
  updateSetting: <K extends keyof OrgSettings>(
    key: K,
    value: OrgSettings[K]
  ) => void;
}) {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Scoring Profile</CardTitle>
          <CardDescription>
            How priority scores are calculated
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <SettingRow
            label="Scoring Profile"
            description="Pre-configured scoring presets"
          >
            <ShadcnSelect
              value={settings.priorityScoringProfile}
              onChange={(v) => updateSetting("priorityScoringProfile", v)}
              options={[
                {
                  value: "developer_friendly",
                  label: "Developer-Friendly",
                },
                { value: "balanced", label: "Balanced (Recommended)" },
                {
                  value: "security_strict",
                  label: "Security-Strict",
                },
                { value: "custom", label: "Custom Weights" },
              ]}
            />
          </SettingRow>

          <Separator />

          <SettingRow
            label="P0 Score Weights"
            description="Adjust how factors contribute to priority"
          >
            <WeightSliders
              weights={{
                cvss: settings.cvssWeight,
                epss: settings.epssWeight,
                kev: settings.kevWeight,
                reachability: settings.reachabilityWeight,
              }}
              onChange={(w) => {
                updateSetting("cvssWeight", w.cvss);
                updateSetting("epssWeight", w.epss);
                updateSetting("kevWeight", w.kev);
                updateSetting("reachabilityWeight", w.reachability);
              }}
            />
          </SettingRow>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Enrichment Settings</CardTitle>
          <CardDescription>
            Configure threat intelligence integration
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <SettingRow
            label="EPSS Elevation Threshold"
            description="Boost priority when exploitation probability exceeds this"
            hint="EPSS scores range from 0 to 1. A score of 0.10 means 10% probability of exploitation in the next 30 days."
          >
            <Slider
              value={settings.epssElevationThreshold}
              onChange={(v) => updateSetting("epssElevationThreshold", v)}
              min={0.01}
              max={0.5}
              step={0.01}
            />
          </SettingRow>

          <Separator />

          <SettingRow
            label="CISA KEV Override"
            description="Auto-boost CVEs in the Known Exploited Vulnerabilities catalog"
          >
            <Switch
              checked={settings.kevOverride}
              onCheckedChange={(v) => updateSetting("kevOverride", v)}
            />
          </SettingRow>

          <Separator />

          <SettingRow
            label="Reachability Analysis"
            description="How strongly reachability influences priority"
          >
            <ShadcnSelect
              value={settings.reachabilityMode}
              onChange={(v) => updateSetting("reachabilityMode", v)}
              options={[
                { value: "off", label: "Off" },
                {
                  value: "conservative",
                  label: "Conservative (High Confidence Only)",
                },
                {
                  value: "aggressive",
                  label: "Aggressive (Partial Evidence)",
                },
              ]}
            />
          </SettingRow>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Context Adjustments</CardTitle>
          <CardDescription>
            Reduce priority for low-risk contexts
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <SettingRow
            label="Test Code Penalty"
            description="Priority adjustment for findings in test files"
          >
            <Slider
              value={settings.testPenalty}
              onChange={(v) => updateSetting("testPenalty", v)}
              min={-30}
              max={0}
            />
          </SettingRow>

          <Separator />

          <SettingRow
            label="Generated Code Penalty"
            description="Priority adjustment for generated/vendor code"
          >
            <Slider
              value={settings.generatedCodePenalty}
              onChange={(v) => updateSetting("generatedCodePenalty", v)}
              min={-30}
              max={0}
            />
          </SettingRow>

          <Separator />

          <SettingRow
            label="Example Code Penalty"
            description="Priority adjustment for example/sample files"
          >
            <Slider
              value={settings.exampleCodePenalty}
              onChange={(v) => updateSetting("exampleCodePenalty", v)}
              min={-30}
              max={0}
            />
          </SettingRow>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Visibility Thresholds</CardTitle>
          <CardDescription>
            Control what appears in PRs vs baseline
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <SettingRow
            label="PR Visibility Threshold"
            description="Minimum P0 score to show in PR feedback"
            hint="Findings below this score still appear in baseline dashboards"
          >
            <Slider
              value={settings.prVisibilityThreshold}
              onChange={(v) => updateSetting("prVisibilityThreshold", v)}
              min={0}
              max={100}
            />
          </SettingRow>

          <Separator />

          <SettingRow
            label="Merge Block Threshold"
            description="P0 score that triggers merge blocking (if enabled)"
          >
            <Slider
              value={settings.mergeBlockThreshold}
              onChange={(v) => updateSetting("mergeBlockThreshold", v)}
              min={50}
              max={100}
            />
          </SettingRow>
        </CardContent>
      </Card>
    </div>
  );
}

function PrCiTab({
  settings,
  updateSetting,
}: {
  settings: OrgSettings;
  updateSetting: <K extends keyof OrgSettings>(
    key: K,
    value: OrgSettings[K]
  ) => void;
}) {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">PR Comments</CardTitle>
          <CardDescription>
            How findings appear on pull requests
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <SettingRow
            label="Comment Style"
            description="How to display findings on PRs"
          >
            <ShadcnSelect
              value={settings.prCommentStyle}
              onChange={(v) => updateSetting("prCommentStyle", v)}
              options={[
                { value: "summary_only", label: "Summary Only" },
                {
                  value: "inline_only",
                  label: "Inline Comments Only",
                },
                {
                  value: "summary_inline",
                  label: "Summary + Inline (Recommended)",
                },
                {
                  value: "checks_only",
                  label: "No Comments (Checks Only)",
                },
              ]}
            />
          </SettingRow>

          <Separator />

          <SettingRow
            label="Max Inline Comments"
            description="Cap inline comments per PR"
          >
            <Slider
              value={settings.maxInlineComments}
              onChange={(v) => updateSetting("maxInlineComments", v)}
              min={1}
              max={50}
            />
          </SettingRow>

          <Separator />

          <SettingRow
            label="Update Existing Comments"
            description="Edit prior comments instead of adding new ones"
          >
            <Switch
              checked={settings.updateExistingComment}
              onCheckedChange={(v) =>
                updateSetting("updateExistingComment", v)
              }
            />
          </SettingRow>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">GitHub Checks</CardTitle>
          <CardDescription>CI integration settings</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <SettingRow
            label="GitHub Check Run"
            description="Publish a check with summary and pass/fail"
          >
            <Switch
              checked={settings.githubCheckRun}
              onCheckedChange={(v) => updateSetting("githubCheckRun", v)}
            />
          </SettingRow>

          <Separator />

          <SettingRow
            label="Merge Gating Mode"
            description="Block merges when thresholds exceeded"
          >
            <ShadcnSelect
              value={settings.mergeGatingMode}
              onChange={(v) => updateSetting("mergeGatingMode", v)}
              options={[
                {
                  value: "warn_only",
                  label: "Warn Only (Advisory)",
                },
                {
                  value: "block",
                  label: "Block When Threshold Exceeded",
                },
              ]}
            />
          </SettingRow>

          <Separator />

          <SettingRow
            label="Auto-Create Tickets"
            description="Open tickets for high-risk findings"
          >
            <Switch
              checked={settings.autoCreateTickets}
              onCheckedChange={(v) => updateSetting("autoCreateTickets", v)}
            />
          </SettingRow>
        </CardContent>
      </Card>
    </div>
  );
}

function SuppressionsTab({
  settings,
  updateSetting,
}: {
  settings: OrgSettings;
  updateSetting: <K extends keyof OrgSettings>(
    key: K,
    value: OrgSettings[K]
  ) => void;
}) {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Suppression Rules</CardTitle>
          <CardDescription>
            Control how findings can be suppressed
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <SettingRow
            label="Allow Suppressions"
            description="Enable moving findings to suppressed state"
          >
            <Switch
              checked={settings.allowSuppressions}
              onCheckedChange={(v) => updateSetting("allowSuppressions", v)}
            />
          </SettingRow>

          <Separator />

          <SettingRow
            label="Require Reason"
            description="Force users to explain why suppressing"
          >
            <Switch
              checked={settings.suppressionRequiresReason}
              onCheckedChange={(v) =>
                updateSetting("suppressionRequiresReason", v)
              }
            />
          </SettingRow>

          <Separator />

          <SettingRow
            label="Require Expiration"
            description="Suppressions must have an end date"
          >
            <Switch
              checked={settings.suppressionRequiresExpiration}
              onCheckedChange={(v) =>
                updateSetting("suppressionRequiresExpiration", v)
              }
            />
          </SettingRow>

          <Separator />

          <SettingRow
            label="Maximum Duration"
            description="Longest allowed suppression period"
          >
            <ShadcnSelect
              value={settings.maxSuppressionDuration.toString()}
              onChange={(v) =>
                updateSetting("maxSuppressionDuration", parseInt(v))
              }
              options={[
                { value: "7", label: "7 days" },
                { value: "30", label: "30 days" },
                { value: "90", label: "90 days" },
                { value: "180", label: "180 days" },
                { value: "365", label: "365 days" },
              ]}
            />
          </SettingRow>

          <Separator />

          <SettingRow
            label="Approval Rules"
            description="When suppressions require approval"
          >
            <ShadcnSelect
              value={settings.suppressionApprovalRules}
              onChange={(v) =>
                updateSetting("suppressionApprovalRules", v)
              }
              options={[
                { value: "none", label: "No Approval Required" },
                {
                  value: "high_critical_require_approval",
                  label: "High/Critical Require Approval",
                },
                {
                  value: "all_require_approval",
                  label: "All Require Approval",
                },
              ]}
            />
          </SettingRow>
        </CardContent>
      </Card>
    </div>
  );
}

function PolicyTab({
  settings,
  updateSetting,
}: {
  settings: OrgSettings;
  updateSetting: <K extends keyof OrgSettings>(
    key: K,
    value: OrgSettings[K]
  ) => void;
}) {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Policy Mode</CardTitle>
          <CardDescription>How policies are enforced</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <SettingRow
            label="Policy Mode"
            description="Report-only or enforce policies"
          >
            <ShadcnSelect
              value={settings.policyMode}
              onChange={(v) => updateSetting("policyMode", v)}
              options={[
                { value: "advisory", label: "Advisory (Report Only)" },
                {
                  value: "enforced",
                  label: "Enforced (Block at Thresholds)",
                },
                {
                  value: "mixed",
                  label: "Mixed (Enforce for Selected Repos)",
                },
              ]}
            />
          </SettingRow>

          <Separator />

          <SettingRow
            label="Required Scan Coverage"
            description="Scan types required for compliance"
          >
            <MultiSelect
              values={settings.requiredScanCoverage}
              onChange={(v) => updateSetting("requiredScanCoverage", v)}
              options={[
                { value: "sast", label: "SAST" },
                { value: "sca", label: "Dependencies" },
                { value: "secrets", label: "Secrets" },
                { value: "iac", label: "IaC" },
                { value: "license", label: "License" },
                { value: "container", label: "Container" },
              ]}
            />
          </SettingRow>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">License Policy</CardTitle>
          <CardDescription>
            Control over open source licenses
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <SettingRow
            label="License Policy Action"
            description="What happens when problematic licenses found"
          >
            <ShadcnSelect
              value={settings.licensePolicyAction}
              onChange={(v) => updateSetting("licensePolicyAction", v)}
              options={[
                { value: "report_only", label: "Report Only" },
                { value: "warn", label: "Warn" },
                { value: "block", label: "Block Merge" },
              ]}
            />
          </SettingRow>

          <Separator />

          <SettingRow
            label="Forbidden Licenses"
            description="Licenses to flag or block"
          >
            <TagInput
              values={settings.forbiddenLicenses}
              onChange={(v) => updateSetting("forbiddenLicenses", v)}
              placeholder="Add license (e.g., AGPL-3.0)"
            />
          </SettingRow>

          <Separator />

          <SettingRow
            label="Unknown License Handling"
            description="What to do when license can't be identified"
          >
            <ShadcnSelect
              value={settings.unknownLicenseHandling}
              onChange={(v) => updateSetting("unknownLicenseHandling", v)}
              options={[
                { value: "ignore", label: "Ignore" },
                { value: "warn", label: "Warn" },
                { value: "forbidden", label: "Treat as Forbidden" },
              ]}
            />
          </SettingRow>
        </CardContent>
      </Card>
    </div>
  );
}

function DataPrivacyTab({
  settings,
  updateSetting,
}: {
  settings: OrgSettings;
  updateSetting: <K extends keyof OrgSettings>(
    key: K,
    value: OrgSettings[K]
  ) => void;
}) {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Data Retention</CardTitle>
          <CardDescription>How long data is kept</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <SettingRow
            label="Findings Retention"
            description="How long to keep historical findings"
          >
            <ShadcnSelect
              value={settings.findingsRetentionDays.toString()}
              onChange={(v) =>
                updateSetting("findingsRetentionDays", parseInt(v))
              }
              options={[
                { value: "90", label: "90 days" },
                { value: "180", label: "180 days" },
                { value: "365", label: "1 year" },
                { value: "730", label: "2 years" },
              ]}
            />
          </SettingRow>

          <Separator />

          <SettingRow
            label="Scan Artifact Retention"
            description="How long to keep scan logs and outputs"
          >
            <ShadcnSelect
              value={settings.scanArtifactRetentionDays.toString()}
              onChange={(v) =>
                updateSetting("scanArtifactRetentionDays", parseInt(v))
              }
              options={[
                { value: "7", label: "7 days" },
                { value: "30", label: "30 days" },
                { value: "90", label: "90 days" },
                { value: "180", label: "180 days" },
              ]}
            />
          </SettingRow>

          <Separator />

          <SettingRow
            label="Operational Log Retention"
            description="How long to keep platform logs"
          >
            <ShadcnSelect
              value={settings.operationalLogRetentionDays.toString()}
              onChange={(v) =>
                updateSetting("operationalLogRetentionDays", parseInt(v))
              }
              options={[
                { value: "7", label: "7 days" },
                { value: "30", label: "30 days" },
                { value: "90", label: "90 days" },
              ]}
            />
          </SettingRow>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Secret Handling</CardTitle>
          <CardDescription>
            How detected secrets are stored
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <SettingRow
            label="Secret Data Handling"
            description="What gets stored when secrets are detected"
          >
            <ShadcnSelect
              value={settings.secretDataHandling}
              onChange={(v) => updateSetting("secretDataHandling", v)}
              options={[
                {
                  value: "masked_only",
                  label: "Masked + Metadata Only (Recommended)",
                },
                {
                  value: "encrypted",
                  label: "Store Encrypted (Admin Access)",
                },
                { value: "none", label: "Metadata Only (No Values)" },
              ]}
            />
          </SettingRow>
        </CardContent>
      </Card>
    </div>
  );
}

function DisplayTab({
  settings,
  updateSetting,
}: {
  settings: OrgSettings;
  updateSetting: <K extends keyof OrgSettings>(
    key: K,
    value: OrgSettings[K]
  ) => void;
}) {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Appearance</CardTitle>
          <CardDescription>Customize the look and feel</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <SettingRow label="Theme" description="Color scheme preference">
            <ShadcnSelect
              value={settings.theme}
              onChange={(v) => {
                updateSetting("theme", v);
                // Apply theme immediately
                if (v === "dark") {
                  document.documentElement.classList.add("dark");
                  document.documentElement.classList.remove("light");
                  localStorage.setItem("paxley-theme", "dark");
                } else if (v === "light") {
                  document.documentElement.classList.remove("dark");
                  document.documentElement.classList.add("light");
                  localStorage.setItem("paxley-theme", "light");
                } else {
                  // System preference
                  const prefersDark = window.matchMedia(
                    "(prefers-color-scheme: dark)"
                  ).matches;
                  if (prefersDark) {
                    document.documentElement.classList.add("dark");
                    document.documentElement.classList.remove("light");
                  } else {
                    document.documentElement.classList.remove("dark");
                    document.documentElement.classList.add("light");
                  }
                  localStorage.removeItem("paxley-theme");
                }
              }}
              options={[
                { value: "system", label: "System" },
                { value: "dark", label: "Dark" },
                { value: "light", label: "Light" },
              ]}
            />
          </SettingRow>

          <Separator />

          <SettingRow
            label="Default Landing Page"
            description="What to show after login"
          >
            <ShadcnSelect
              value={settings.defaultLandingPage}
              onChange={(v) => updateSetting("defaultLandingPage", v)}
              options={[
                { value: "priority_queue", label: "Priority Queue" },
                { value: "dashboard", label: "Dashboard Overview" },
                { value: "repositories", label: "Repositories" },
                { value: "findings", label: "All Findings" },
              ]}
            />
          </SettingRow>
        </CardContent>
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Settings Page
// ─────────────────────────────────────────────────────────────────────────────

export default function Settings() {
  const [activeTab, setActiveTab] = useState<TabId>("organization");
  const [settings, setSettings] = useState<OrgSettings>(defaultSettings);
  const [hasChanges, setHasChanges] = useState(false);
  const queryClient = useQueryClient();

  // Load settings from API
  const { isLoading, error } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => {
      try {
        const data = await apiFetch<OrgSettings>({ path: "/v1/settings" });
        setSettings({ ...defaultSettings, ...data });
        return data;
      } catch (e: any) {
        // Only swallow 404 (settings don't exist yet) -- re-throw all other errors
        if (e?.status === 404) {
          return defaultSettings;
        }
        throw e;
      }
    },
  });

  // Save settings mutation
  const saveMutation = useMutation({
    mutationFn: async (newSettings: OrgSettings) => {
      return apiFetch({
        method: "PUT",
        path: "/v1/settings",
        body: newSettings,
      });
    },
    onSuccess: () => {
      setHasChanges(false);
      queryClient.invalidateQueries({ queryKey: ["settings"] });
    },
  });

  const updateSetting = <K extends keyof OrgSettings>(
    key: K,
    value: OrgSettings[K]
  ) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    saveMutation.mutate(settings);
  };

  return (
    <Layout>
      <PageContainer>
        <div className="flex items-center justify-between mb-6">
          <PageHeader
            title="Settings"
            description="Configure your organization's security scanning preferences"
          />

          {/* Save Button */}
          <Button
            onClick={handleSave}
            disabled={!hasChanges || saveMutation.isPending}
            className="gap-2"
          >
            {saveMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : saveMutation.isSuccess && !hasChanges ? (
              <>
                <CheckCircle2 className="h-4 w-4" />
                Saved
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Save Changes
              </>
            )}
          </Button>
        </div>

        {/* Error Banner */}
        {saveMutation.isError && (
          <Card className="mb-6 border-destructive/50 bg-destructive/5">
            <CardContent className="flex items-center gap-3 p-4">
              <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0" />
              <div>
                <div className="font-medium text-destructive">
                  Failed to save settings
                </div>
                <div className="text-sm text-muted-foreground">
                  {(saveMutation.error as Error)?.message ||
                    "Please try again"}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as TabId)}
          className="flex flex-col md:flex-row gap-6"
        >
          {/* Vertical Tab Navigation (desktop) / Horizontal (mobile) */}
          <TabsList className="flex md:flex-col h-auto md:w-56 flex-shrink-0 bg-card border rounded-lg p-1.5 md:p-2 overflow-x-auto md:overflow-x-visible md:sticky md:top-6 md:self-start">
            {tabsConfig.map((tab) => {
              const Icon = tab.icon;
              return (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  className={cn(
                    "w-full justify-start gap-3 px-3 py-2.5 text-sm font-medium rounded-md",
                    "data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:shadow-none",
                    "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  )}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  <span className="hidden md:inline">{tab.label}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {isLoading ? (
              <SettingsLoadingSkeleton />
            ) : (
              <>
                <TabsContent value="organization" className="mt-0">
                  <OrganizationTab
                    settings={settings}
                    updateSetting={updateSetting}
                  />
                </TabsContent>
                <TabsContent value="notifications" className="mt-0">
                  <NotificationsTab
                    settings={settings}
                    updateSetting={updateSetting}
                  />
                </TabsContent>
                <TabsContent value="scanning" className="mt-0">
                  <ScanningTab
                    settings={settings}
                    updateSetting={updateSetting}
                  />
                </TabsContent>
                <TabsContent value="priority" className="mt-0">
                  <PriorityScoringTab
                    settings={settings}
                    updateSetting={updateSetting}
                  />
                </TabsContent>
                <TabsContent value="pr-ci" className="mt-0">
                  <PrCiTab
                    settings={settings}
                    updateSetting={updateSetting}
                  />
                </TabsContent>
                <TabsContent value="suppressions" className="mt-0">
                  <SuppressionsTab
                    settings={settings}
                    updateSetting={updateSetting}
                  />
                </TabsContent>
                <TabsContent value="policy" className="mt-0">
                  <PolicyTab
                    settings={settings}
                    updateSetting={updateSetting}
                  />
                </TabsContent>
                <TabsContent value="data" className="mt-0">
                  <DataPrivacyTab
                    settings={settings}
                    updateSetting={updateSetting}
                  />
                </TabsContent>
                <TabsContent value="display" className="mt-0">
                  <DisplayTab
                    settings={settings}
                    updateSetting={updateSetting}
                  />
                </TabsContent>
              </>
            )}
          </div>
        </Tabs>
      </PageContainer>
    </Layout>
  );
}
